import React, { useEffect, useMemo, useState } from 'react'
import moment from 'moment/moment'
import { Button, Modal, DatePicker, Form, Input, Spin, Space, message, Upload, Select, Typography } from 'antd'
import EstimateItemInput from '../estimateItem/EstimateItemInput'
import { MinusCircleOutlined, UploadOutlined } from '@ant-design/icons'
import DecimalInput from '../DecimalInput'
import { collectItems, createAct, updateAct } from '../../services/acts'
import { CategoryProvider } from '../../app/providers/Category'

const { Text } = Typography

const { Option } = Select

const ActModal = ({ showModal, project, onClose, onSuccess, act }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [form] = Form.useForm()
  const [file, setFile] = useState(null)
  const [actData, setActData] = useState(null)
  const [lostEstimateItems, setLostEstimateItems] = useState([])

  useEffect(() => setActData(act), [act])

  useEffect(() => {
    form.resetFields()
    if (actData?.act_items.length === 0) {
      setIsLoading(true)
      // получить элементы из файла
      collectItems(actData.id)
        .then(({ data }) => {
          form.setFieldsValue({ actItems: data.actItems })
          setLostEstimateItems(data.lostEstimateItems)
        })
        .catch((error) => {
          message.error('Ошибка при обновлении акта')
        })
        .finally(() => setIsLoading(false))
    }
  }, [actData?.id])

  const initFields = useMemo(
    () =>
      actData
        ? {
            number: actData.number,
            date: moment(actData.date || new Date()),
            actItems: actData.act_items?.map((item) => ({
              id: item.id,
              estimate: {
                key: item?.estimate?.id,
                value: item?.estimate?.name,
              },
              amount: item.amount,
              price: item.price,
              vatType: item.vatType,
              vatValue: item.vatValue,
              unit: item.unit,
            })),
          }
        : {},
    [actData]
  )

  const beforeUpload = (file) => {
    setFile(file)
    // Prevent automatic upload
    return false
  }

  const onFinish = (values) => {
    const formData = new FormData()

    const data = {
      project: actData ? actData.project.id : project,
      date: values.date,
      number: values.number,
      actItems: values.actItems,
    }
    formData.append('data', JSON.stringify(data))

    if (file) {
      formData.append('files.file', file, file?.name)
    }

    if (actData) {
      setIsLoading(true)
      updateAct(actData?.id, { data })
        .then(() => {
          if (onSuccess) {
            onSuccess()
          }
          clearData()
          message.success('Акт успешно обновлен')
        })
        .catch((error) => {
          message.error('Ошибка при обновлении акта')
        })
        .finally(() => setIsLoading(false))
    } else {
      createAct({
        data: formData,
      })
        .then(({ data }) => {
          setActData(data)
          message.success('Акт успешно создан')
        })
        .catch((error) => {
          message.error('Ошибка при создании акта')
        })
    }
  }

  const onEstimateSelect = (estimate, key) => {
    const fields = form.getFieldsValue()
    fields.actItems[key].unit = estimate.unit
    fields.actItems[key].price = estimate.price
    form.setFieldsValue(fields)
  }

  const clearData = () => {
    setActData(null)
    setLostEstimateItems([])
    form.resetFields()
  }

  const onFormClose = () => {
    clearData()
    onClose()
  }

  return (
    <Modal
      title={actData ? `Акт номер ${actData.number}` : 'Новый акт'}
      width={1200}
      visible={showModal}
      onCancel={onFormClose}
      forceRender
      footer={[
        <Button key="back" onClick={onFormClose} disabled={isLoading}>
          Отмена
        </Button>,
        <Button key="agree" type="primary" onClick={() => form.submit()} disabled={isLoading}>
          {actData ? 'Сохранить' : 'Построить'}
        </Button>,
      ]}
    >
      <Spin spinning={isLoading}>
        <Form initialValues={initFields} form={form} layout="vertical" onFinish={onFinish}>
          <Space>
            <Form.Item style={{ minWidth: 250 }} label="Номер" name="number">
              <Input />
            </Form.Item>
            <Form.Item label="Дата" name="date">
              <DatePicker />
            </Form.Item>
            <Form.Item label="Файл с данными акта" name="filedata">
              <Upload maxCount={1} showUploadList={true} beforeUpload={beforeUpload}>
                <Button icon={<UploadOutlined />}>Загрузить</Button>
              </Upload>
            </Form.Item>
          </Space>
          {/*
          <Form.Item label="Файл акта" name="file">
            <Upload maxCount={1} beforeUpload={beforeUpload}>
              <Button icon={<UploadOutlined />}>Загрузить</Button>
            </Upload>
          </Form.Item>
          */}
          <CategoryProvider projectId={project}>
            <Form.List name="actItems">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }, index) => (
                    <div key={key}>
                      <Space size={0}>
                        <Form.Item {...restField} label="Идентификатор" name={[name, 'id']} hidden>
                          <Input />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          label="Позиция из сметы"
                          style={{ minWidth: 400, marginRight: 15 }}
                          name={[name, 'estimate']}
                          rules={[{ required: true, message: 'Поле обязательно' }]}
                        >
                          <EstimateItemInput
                            project={actData ? actData?.project?.id : project}
                            loadOnPress={true}
                            onEstimateSelect={(estimate) => onEstimateSelect(estimate, index)}
                          />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          label="Количество"
                          name={[name, 'amount']}
                          rules={[{ required: true, message: 'Поле обязательно' }]}
                        >
                          <DecimalInput style={{ minWidth: 150, marginRight: 15 }} />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          label="Цена за ед."
                          name={[name, 'price']}
                          rules={[{ required: true, message: 'Поле обязательно' }]}
                        >
                          <DecimalInput style={{ marginRight: 15, maxWidth: 150, minWidth: 100 }} />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          label="НДС"
                          name={[name, 'vatType']}
                          style={{ marginRight: 15, minWidth: 150 }}
                        >
                          <Select dropdownMatchSelectWidth={false}>
                            <Option value="none">Без НДС</Option>
                            <Option value="up">НДС сверху</Option>
                            <Option value="include">НДС в сумме</Option>
                          </Select>
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          label="% НДС"
                          name={[name, 'vatValue']}
                          style={{ marginRight: 15, minWidth: 100 }}
                        >
                          <Select dropdownMatchSelectWidth={false}>
                            <Option value={0}>0%</Option>
                            <Option value={10}>10%</Option>
                            <Option value={20}>20%</Option>
                          </Select>
                        </Form.Item>
                        <Form.Item
                          style={{ minWidth: 50, marginRight: 15 }}
                          label="Единица измерения"
                          name={[name, 'unit']}
                        >
                          <Input disabled />
                        </Form.Item>
                        <Form.Item label=" ">
                          <MinusCircleOutlined onClick={() => remove(name)} />
                        </Form.Item>
                      </Space>
                    </div>
                  ))}
                  <Form.Item style={{ paddingTop: 15 }}>
                    <Button disabled={isLoading} type="dashed" onClick={() => add()} block>
                      Добавить позицию в акт
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </CategoryProvider>
        </Form>
        {lostEstimateItems?.map(({ name, category }) => (
          <div>
            <Text type={'danger'}>
              Не удалось найти позицию: <b>{name}</b> для категории: <b>{category}</b>
            </Text>
          </div>
        ))}
      </Spin>
    </Modal>
  )
}

export default ActModal
